import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { normalizeActionAIModelSelections } from '../../Utils/actionAIModelUtils';

const ActionScopeContext = createContext('event');
const ActionSettingsPayloadContext = createContext(null);
const ActionSettingsCommandContext = createContext(null);
const ActionCaseIdContext = createContext(null);
const ActionEditorControlsContext = createContext(null);
const ActionEditorGeneralSettingsContext = createContext(null);
const ActionTypeContext = createContext('');
const ActionAIModelContext = createContext(null);

export const ActionScopeProvider = ({
  scope = 'event',
  caseid = null,
  setting = null,
  sendActionCommand = null,
  registerEditorControls = null,
  editorGeneralSettings = null,
  actionType = '',
  isPluginAction = false,
  children
}) => {
  const [actionAIModels, setActionAIModels] = useState(() => (
    normalizeActionAIModelSelections(setting?.customData ?? setting?.data ?? {})
  ));
  const settingKey = useMemo(() => (
    `${setting?.id || ''}:${JSON.stringify(setting?.customData ?? setting?.data ?? {})}`
  ), [setting]);

  useEffect(() => {
    setActionAIModels(normalizeActionAIModelSelections(setting?.customData ?? setting?.data ?? {}));
  }, [settingKey, setting?.customData, setting?.data]);

  const setActionAIModel = useCallback((modelType, modelId) => {
    const fieldName = {
      text: 'aiTextModelId',
      vision: 'aiVisionModelId',
      audio: 'aiAudioModelId',
    }[modelType];
    if (!fieldName) {
      return;
    }
    setActionAIModels((current) => ({
      ...current,
      [fieldName]: modelId ? String(modelId) : '',
    }));
  }, []);

  const actionAIModelContextValue = useMemo(() => ({
    actionAIModels,
    setActionAIModels,
    setActionAIModel,
    isPluginAction,
  }), [actionAIModels, isPluginAction, setActionAIModel]);

  return (
    <ActionEditorGeneralSettingsContext.Provider value={editorGeneralSettings}>
      <ActionEditorControlsContext.Provider value={registerEditorControls}>
        <ActionSettingsCommandContext.Provider value={sendActionCommand}>
          <ActionSettingsPayloadContext.Provider value={setting}>
            <ActionCaseIdContext.Provider value={caseid}>
              <ActionTypeContext.Provider value={actionType}>
                <ActionAIModelContext.Provider value={actionAIModelContextValue}>
                  <ActionScopeContext.Provider value={scope || 'event'}>
                    {children}
                  </ActionScopeContext.Provider>
                </ActionAIModelContext.Provider>
              </ActionTypeContext.Provider>
            </ActionCaseIdContext.Provider>
          </ActionSettingsPayloadContext.Provider>
        </ActionSettingsCommandContext.Provider>
      </ActionEditorControlsContext.Provider>
    </ActionEditorGeneralSettingsContext.Provider>
  );
};

export const useActionScope = () => useContext(ActionScopeContext) || 'event';
export const useActionSettingsPayload = () => useContext(ActionSettingsPayloadContext);
export const useActionSettingsCommand = () => useContext(ActionSettingsCommandContext);
export const useActionCaseId = () => useContext(ActionCaseIdContext);
export const useActionEditorControls = () => useContext(ActionEditorControlsContext);
export const useActionEditorGeneralSettings = () => useContext(ActionEditorGeneralSettingsContext);
export const useActionType = () => useContext(ActionTypeContext) || '';
export const useActionAIModels = () => useContext(ActionAIModelContext) || {
  actionAIModels: normalizeActionAIModelSelections(),
  setActionAIModels: () => {},
  setActionAIModel: () => {},
  isPluginAction: false,
};

export default ActionScopeContext;
